import { StrictMode, Suspense, useEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  Routes,
  useLocation,
} from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import { AppRoutes } from '~/routes'
import { SeoContentSection } from '~/components/SeoContentSection/SeoContentSection'
import { Footer } from '~/components/Footer/Footer'
import { ScrollToTop } from '~/components/ScrollToTop/ScrollToTop'
import { CartProvider } from '~/context/cartContext'
import { preloadPBRTextures } from '~/components/ThreeDModel/assets/textureCache'
import { FAQ_RO, FAQ_RU } from '~/seo/faq'
import ro from '../locales/ro.json'
import ru from '../locales/ru.json'
import '~/components/Layout/Layout.css'
import '~/components/Landing/Dulap.css'

const SITE_ORIGIN = 'https://dulap.md'
const OG_IMAGE = `${SITE_ORIGIN}/og-default.jpg`

const localeMap: Record<string, Record<string, string>> = { ro, ru }
const hreflangCode: Record<'ro' | 'ru', string> = {
  ro: 'ro-MD',
  ru: 'ru-MD',
}

function parseLocaleAndPath(rawUrl: string): {
  locale: 'ro' | 'ru'
  pathWithoutLocale: string
} {
  const cleanPath = (rawUrl || '/').split('?')[0].split('#')[0] || '/'
  const ruMatch = cleanPath.match(/^\/ru(\/.*)?$/)
  if (ruMatch) {
    const rest = ruMatch[1] ?? '/'
    return { locale: 'ru', pathWithoutLocale: rest === '' ? '/' : rest }
  }
  const roMatch = cleanPath.match(/^\/ro(\/.*)?$/)
  if (roMatch) {
    const rest = roMatch[1] ?? '/'
    return { locale: 'ro', pathWithoutLocale: rest === '' ? '/' : rest }
  }
  return { locale: 'ro', pathWithoutLocale: cleanPath }
}

function metaCodeFromPath(pathWithoutLocale: string): string {
  return (
    pathWithoutLocale.replace(/^\//, '').replace(/\/$/, '').replaceAll('/', '.') ||
    'default'
  )
}

function AppShell() {
  const location = useLocation()
  const asPath = `${location.pathname}${location.search}`
  const { locale, pathWithoutLocale } = parseLocaleAndPath(asPath)
  const messages = localeMap[locale] ?? ro

  const metaCode = useMemo(() => metaCodeFromPath(pathWithoutLocale), [pathWithoutLocale])
  const metaTitle =
    messages[`meta_title.${metaCode}`] ?? messages['meta_title.default']
  const metaDescription =
    messages[`meta_description.${metaCode}`] ??
    messages['meta_description.default']

  const path = pathWithoutLocale === '/' ? '/' : pathWithoutLocale.replace(/\/$/, '')
  const localeUrl = (loc: 'ro' | 'ru') =>
    loc === 'ro'
      ? `${SITE_ORIGIN}${path === '/' ? '/' : path}`
      : `${SITE_ORIGIN}/${loc}${path === '/' ? '' : path}`
  const canonicalUrl = localeUrl(locale)
  const isHome = pathWithoutLocale === '/'
  const faqJson = isHome ? JSON.stringify(locale === 'ru' ? FAQ_RU : FAQ_RO) : null

  // <html lang> must reflect the active locale so search engines and
  // assistive tech read the page in the right language. React doesn't
  // render the <html> element from JSX, so update it imperatively.
  // The prerenderer waits for paint, so this is captured in the static HTML.
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Signal to scripts/prerender.mjs that React has hoisted meta tags and
  // the locale-driven <html lang> is set. Replaces a fragile fixed timeout
  // wait. Deps cover every value that affects SEO output.
  useEffect(() => {
    document.documentElement.setAttribute('data-seo-ready', 'true')
  }, [metaTitle, metaDescription, canonicalUrl, locale, faqJson])

  useEffect(() => {
    preloadPBRTextures()
  }, [])

  return (
    <IntlProvider locale={locale} messages={messages}>
      <CartProvider>
        <ScrollToTop />
        {metaTitle ? <title>{metaTitle}</title> : null}
        {metaDescription ? <meta name="description" content={metaDescription} /> : null}
        {metaTitle ? <meta property="og:title" content={metaTitle} /> : null}
        {metaDescription ? (
          <meta property="og:description" content={metaDescription} />
        ) : null}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="Dulap Studio" />
        <meta property="og:locale" content={locale === 'ru' ? 'ru_MD' : 'ro_MD'} />
        <meta name="twitter:card" content="summary_large_image" />
        {metaTitle ? <meta name="twitter:title" content={metaTitle} /> : null}
        {metaDescription ? (
          <meta name="twitter:description" content={metaDescription} />
        ) : null}
        <meta name="twitter:image" content={OG_IMAGE} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang={hreflangCode.ro} href={localeUrl('ro')} />
        <link rel="alternate" hrefLang={hreflangCode.ru} href={localeUrl('ru')} />
        <link rel="alternate" hrefLang="x-default" href={localeUrl('ro')} />
        {faqJson ? (
          <script
            type="application/ld+json"
            // JSON-LD payload is build-time constant from FAQ_RO / FAQ_RU.
            dangerouslySetInnerHTML={{ __html: faqJson }}
          />
        ) : null}

        <div className="appShell">
          <div className="appMain">
            <Suspense
              fallback={
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Opaque, on top of the always-mounted <Footer>. Every route
                    // is lazy, so while a chunk loads .appMain collapses and the
                    // footer would flash mid-screen behind a transparent overlay.
                    // Cover it so there's no visible footer jump during load.
                    background: '#fffbf7',
                    zIndex: 9999,
                  }}
                >
                  <img src="/cdn/logo.svg" alt="Dulap" width={120} height={120} />
                </div>
              }
            >
              <Routes>{AppRoutes()}</Routes>
            </Suspense>

            {/*
              Single, prerender-safe product/SEO block for configurator pages.
              Rendered OUTSIDE the <Suspense> above (every route is lazy-loaded,
              so prerender.mjs snapshots the DOM during the fallback and never
              sees page-component content) and BEFORE <Footer>. Configurator
              only — /catalog carries its own inline h1 and no text block, so
              the visual product grid isn't followed by an alien copy block.
            */}
            {metaCode.startsWith('configurator.') && (
              <SeoContentSection page={metaCode} />
            )}
          </div>

          <Footer />
        </div>
      </CartProvider>
    </IntlProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  </StrictMode>
)
